"use client";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogContext {
  endpoint?: string;
  statusCode?: number;
  durationMs?: number;
  profileId?: string;
  transactionId?: string;
  error?: {
    message: string;
    stack?: string;
    name?: string;
  };
  [key: string]: unknown;
}

export function serializeError(err: unknown): LogContext["error"] {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }
  if (err !== null && typeof err === "object") {
    const o = err as Record<string, unknown>;
    return {
      name: typeof o.name === "string" ? o.name : undefined,
      message: typeof o.message === "string" ? o.message : JSON.stringify(err),
      ...(typeof o.code !== "undefined" && { code: o.code }),
      ...(typeof o.details !== "undefined" && { details: o.details })
    };
  }
  return { message: String(err) };
}

const isDev = process.env.NODE_ENV === "development";

function emit(
  level: LogLevel,
  module: string,
  message: string,
  context?: LogContext
): void {
  if (isDev) {
    // Dev: readable console output, no network round-trip
    const prefix = `${level.toUpperCase().padEnd(5)} [${module}] ${message}`;
    const consoleFn =
      level === "error"
        ? console.error
        : level === "warn"
          ? console.warn
          : level === "debug"
            ? console.debug
            : console.log;
    if (context) {
      consoleFn(prefix, context);
    } else {
      consoleFn(prefix);
    }
    return;
  }

  // Production: ship to /api/logs (fire-and-forget)
  fetch("/api/logs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ level, module, message, context }),
    keepalive: true // survives page navigations
  }).catch(() => {
    // swallow silently — logging must never break the app
  });
}

export interface ClientLogger {
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, context?: LogContext): void;
  flush(): Promise<void>;
}

export function createClientLogger(module: string): ClientLogger {
  return {
    debug: (msg, ctx) => emit("debug", module, msg, ctx),
    info: (msg, ctx) => emit("info", module, msg, ctx),
    warn: (msg, ctx) => emit("warn", module, msg, ctx),
    error: (msg, ctx) => emit("error", module, msg, ctx),
    flush: () => Promise.resolve() // no-op: each log is a separate fire-and-forget request
  };
}
